import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Dialog, DialogContent, IconButton } from "@mui/material";
import { PDFViewer } from "@react-pdf/renderer";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { VIEW_GOVERNMENT_INVOICE, EDIT_GOVERNMENT_INVOICE, DELETE_GOVERNMENT_INVOICE } from "../../constants";
import { getInvoiceTableColumns } from "../../pages/project/constants";
import { CompanyService, CRMService, ProjectService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { get_date } from "../generalComponets";
import { GovernmentInvoicePdf, getInvoicePdfPayload } from "../invoices/GovernmentInvoicePdf.template";
import { handlePdfDownload, Listing } from "../pdf";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectClientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, data: null });
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const { paginationModel: paginationState, setPaginationModel: setPaginationState } = usePersistedTableState({
    tableId: "project-invoices",
    defaults: {},
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    invoiceId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasViewInvoice = useHasPermission(VIEW_GOVERNMENT_INVOICE);
  const hasEditInvoice = useHasPermission(EDIT_GOVERNMENT_INVOICE);
  const hasDeleteInvoice = useHasPermission(DELETE_GOVERNMENT_INVOICE);

  const fetchData = useCallback(
    async (invoiceFilters = {}) => {
      setLoading(true);
      try {
        const { data: invoices } = await CRMService.getClientInvoices({
          ...invoiceFilters,
          projectId: id,
          sortBy: "date",
          sortOrder: "desc",
        });
        setInvoices(invoices.rows);
        setTotalCount(invoices.count);
      } catch (error) {
        toast.error("Failed to fetch invoices");
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
    });
  }, [fetchData, paginationState]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Invoices" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Invoices",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleEdit = (data) => {
    navigate(`/client/invoices/edit/${data.id}`);
  };

  const openDeleteModal = (entity) => {
    setDeleteModal({
      isOpen: true,
      invoiceId: entity?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      invoiceId: null,
    });
  };

  const closePdfModal = () => setPdfModal({ open: false, data: null });

  const deleteInvoice = async () => {
    try {
      await CRMService.deleteClientInvoice(deleteModal.invoiceId);
      toast.success("Invoice deleted successfully");
      closeDeleteModal();
      fetchData({
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
      });
    } catch (error) {
      console.error("🚀 ~ deleteInvoice ~ error:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const handleRowClick = async (row) => {
    if (!hasViewInvoice) return;
    setLoading(true);
    try {
      const { data: invoice } = await CRMService.getClientInvoiceById(row.id);
      const { data: project } = await ProjectService.getProjectById(invoice?.projectId);
      const { data: client } = await CRMService.getClientById(invoice?.clientId);
      const { data: company } = await CompanyService.getCompanyDetails(user?.company?.id);

      const pdfData = getInvoicePdfPayload(invoice, project, client, company);
      setPdfModal({ open: true, data: pdfData });
    } catch (error) {
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleActions = (data, actionType) => {
    if (actionType === "edit") handleEdit(data);
    else if (actionType === "delete") openDeleteModal(data);
  };

  const columnHeaders = getInvoiceTableColumns(handleActions, hasEditInvoice, hasDeleteInvoice);

  const rows = invoices.map((item, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: item.id,
    invoiceNumber: item?.invoiceNumber,
    date: get_date(item?.date),
    amount: Math.round(Number(item?.totalAmount || 0)),
  }));

  return (
    <>
      <CardLayout>
        <MTable
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={handleRowClick}
        />
      </CardLayout>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={deleteInvoice}
        message="Want to delete this invoice?"
      />

      {/* PDF Modal */}
      <Dialog
        open={pdfModal.open}
        onClose={closePdfModal}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { height: "100vh" } } }}
      >
        <IconButton onClick={closePdfModal} sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0, height: "100%" }}>
          {pdfModal.data ? (
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <GovernmentInvoicePdf data={pdfModal.data} />
            </PDFViewer>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
